/**
 * useFocusTrap — intrappola il Tab dentro un contenitore modale e ripristina
 * il focus all'elemento attivo precedente alla chiusura.
 *
 * Uso:
 *   const panelRef = useRef(null);
 *   useFocusTrap(open, panelRef, { onEscape: () => setOpen(false) });
 *   ...
 *   <div ref={panelRef} role="dialog" aria-modal="true">...</div>
 *
 * Opzioni:
 *   - onEscape: callback su tasto Esc (opzionale — se il componente gestisce
 *     già Esc altrove, ometterla per evitare doppia chiusura)
 *   - initialFocus: se false, non sposta il focus all'apertura (default true)
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'textarea:not([disabled])', 'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useFocusTrap(active, containerRef, { onEscape, initialFocus = true } = {}) {
  const previousFocus = useRef(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    previousFocus.current = document.activeElement;

    const getEls = () => {
      const c = containerRef.current;
      return c ? [...c.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement) : [];
    };

    if (initialFocus) {
      // Sposta il focus dentro il modale (primo elemento utile)
      const els = getEls();
      if (els.length && !containerRef.current?.contains(document.activeElement)) {
        els[0].focus();
      }
    }

    const onKey = (e) => {
      if (e.key === 'Escape' && onEscapeRef.current) {
        e.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = getEls();
      if (!els.length) return;
      const idx = els.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0) {
        e.preventDefault();
        els[els.length - 1].focus();
      } else if (!e.shiftKey && (idx === els.length - 1 || idx === -1)) {
        e.preventDefault();
        els[0].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const prev = previousFocus.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [active, containerRef, initialFocus]);
}

export default useFocusTrap;
